<template>
  <div>
    <v-overlay :value="loading" absolute :opacity="0.8" z-index="10">
      <div class="text-center">
        <v-progress-circular indeterminate size="48" color="primary" class="mb-3"></v-progress-circular>
        <div class="text-body-1 white--text">Loading pipelines...</div>
      </div>
    </v-overlay>

    <v-card v-if="lists && lists.length > 0" class="mx-auto" flat :style="{ opacity: loading ? 0.5 : 1 }">

      <v-snackbar v-model="snackbar" timeout="3000" :color="snackbarColor" absolute top>
        {{ snackbarMessage }}
      </v-snackbar>

      <v-card-title class="d-flex justify-space-between align-center pa-4 pb-3">
        <div>
          <div class="text-h6 font-weight-bold mb-1">Manage Pipelines</div>
          <div class="text-caption text--secondary">
            <v-icon x-small class="mr-1">mdi-file-document-multiple</v-icon>
            {{ tenderIds.length }} {{ tenderIds.length === 1 ? 'notice' : 'notices' }} selected
          </div>
        </div>
        <v-btn icon small @click="$emit('hide-list-manager', true)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4 pt-3">
        <!-- Create New Pipeline Section -->
        <div class="mb-4">
          <v-expansion-panels v-model="newPipelinePanel" flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="px-3 py-2" style="background-color: rgba(78, 184, 136, 0.1);">
                <template v-slot:default="{ open }">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" :color="open ? 'primary' : 'secondary'" size="24">mdi-plus-circle</v-icon>
                    <span class="font-weight-bold text-body-1">Create New Pipeline</span>
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="px-3 pb-3 pt-3" style="background-color: rgba(78, 184, 136, 0.05);">
                <v-text-field v-model="newPipelineName" outlined label="Pipeline name" hide-details
                  append-icon="mdi-check" @click:append="postPipeline()" @keyup.enter="postPipeline()"
                  :loading="loading" dense autofocus></v-text-field>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <!-- Search Section -->
        <div class="mb-4">
          <v-text-field v-model="searchQuery" outlined prepend-inner-icon="mdi-magnify" label="Search pipelines..."
            hide-details clearable dense></v-text-field>
        </div>

        <v-divider class="mb-4"></v-divider>

        <!-- Pipelines List Section -->
        <div class="mb-3">
          <div class="text-subtitle-2 font-weight-medium mb-3 d-flex align-center">
            Available Pipelines
            <v-chip x-small class="ml-2" color="grey lighten-2">{{ filteredLists.length }}</v-chip>
          </div>
        </div>

        <div class="pipeline-list" style="max-height: 450px; overflow-y: auto; overflow-x: hidden;">
          <div v-if="filteredLists.length === 0" class="text-center py-8 text--secondary">
            <v-icon large class="mb-3" color="grey lighten-1">mdi-magnify</v-icon>
            <div class="text-subtitle-1 mb-1">No pipelines found</div>
            <div class="text-body-2">Try adjusting your search or create a new pipeline</div>
          </div>

          <v-row v-else dense no-gutters>
            <v-col v-for="pipeline in filteredLists" :key="pipeline.id" cols="12" class="px-1 mb-2">
              <v-card
                :class="['pipeline-card', getPipelineItemClass(pipeline), { 'pipeline-card-even': filteredLists.indexOf(pipeline) % 2 === 0, 'pipeline-card-odd': filteredLists.indexOf(pipeline) % 2 === 1 }]"
                flat :elevation="0">
                <v-card-text class="pa-3">
                  <!-- Pipeline Header -->
                  <div class="d-flex align-center justify-space-between mb-2 flex-wrap">
                    <div class="d-flex align-center flex-grow-1 min-width-0 mr-2">
                      <v-icon class="mr-2 flex-shrink-0" :color="getBookmarkIconColor(pipeline)" size="20">
                        mdi-bookmark
                      </v-icon>
                      <div class="text-body-2 font-weight-medium text-truncate">
                        {{ pipeline.name }}
                      </div>
                    </div>
                    <v-chip x-small :color="getPipelineStatus(pipeline).color"
                      :text-color="getPipelineStatus(pipeline).textColor" class="flex-shrink-0">
                      {{ getPipelineStatus(pipeline).text }}
                    </v-chip>
                  </div>

                  <!-- Pipeline Status Info -->
                  <div class="mb-2">
                    <div class="text-caption text--secondary mb-1">
                      <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                      <span v-if="getTenderCountInPipeline(pipeline) > 0">
                        {{ getTenderCountInPipeline(pipeline) }} of {{ tenderIds.length }}
                        {{ getTenderCountInPipeline(pipeline) === 1 ? 'tender' : 'tenders' }}
                        in pipeline
                      </span>
                      <span v-else>
                        No selected notices in this pipeline
                      </span>
                    </div>

                    <!-- Show which specific tenders are in this pipeline -->
                    <div v-if="getTendersInPipeline(pipeline).length > 0" class="mt-1">
                      <div class="text-caption text--secondary mb-1 font-weight-medium">Notices:</div>
                      <div class="d-flex flex-wrap">
                        <v-tooltip v-for="tender in getTendersInPipeline(pipeline)" :key="tender.id" top>
                          <template v-slot:activator="{ on }">
                            <v-chip v-on="on" x-small class="mr-1 mb-1" color="primary" outlined
                              style="max-width: 150px;">
                              <span class="text-truncate d-inline-block" style="max-width: 130px;">
                                {{ tender.title || `#${tender.id}` }}
                              </span>
                            </v-chip>
                          </template>
                          <span>{{ tender.title || `Tender #${tender.id}` }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="d-flex align-center flex-wrap">
                    <v-btn
                      v-if="!pipelinesToRemove.includes(pipeline.id) && (!hasAllTendersInPipeline(pipeline) || pipelinesToAdd.includes(pipeline.id))"
                      x-small :color="pipelinesToAdd.includes(pipeline.id) ? 'success' : 'primary'"
                      :outlined="!pipelinesToAdd.includes(pipeline.id)"
                      :depressed="pipelinesToAdd.includes(pipeline.id)" @click="toggleAddToPipeline(pipeline.id)"
                      class="mr-1 mb-1">
                      <v-icon x-small left>{{ pipelinesToAdd.includes(pipeline.id) ? 'mdi-check-circle' :
                        'mdi-plus-circle'
                        }}</v-icon>
                      {{ pipelinesToAdd.includes(pipeline.id) ? 'Will Add' : 'Add All' }}
                    </v-btn>

                    <v-btn
                      v-if="!pipelinesToAdd.includes(pipeline.id) && (hasAnyTendersInPipeline(pipeline) || pipelinesToRemove.includes(pipeline.id))"
                      x-small color="error" :outlined="!pipelinesToRemove.includes(pipeline.id)"
                      :depressed="pipelinesToRemove.includes(pipeline.id)"
                      @click="toggleRemoveFromPipeline(pipeline.id)" class="mb-1">
                      <v-icon x-small left>mdi-minus-circle</v-icon>
                      {{ pipelinesToRemove.includes(pipeline.id) ? 'Will Remove' : 'Remove All' }}
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-divider class="mt-4 mb-3"></v-divider>

        <!-- Summary of changes -->
        <div v-if="hasChanges" class="change-summary pa-3 mb-3"
          style="background: linear-gradient(135deg, rgba(78, 184, 136, 0.1) 0%, rgba(78, 184, 136, 0.05) 100%); border-left: 4px solid #4EB888; border-radius: 4px;">
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" class="mr-2" small>mdi-clipboard-text-outline</v-icon>
            <span class="text-body-2 font-weight-bold">Summary of Changes</span>
          </div>
          <v-row dense no-gutters>
            <v-col v-if="pipelinesToAdd.length > 0" cols="12" md="6" class="pr-md-2">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="success" small>mdi-plus-circle</v-icon>
                <div>
                  <div class="text-caption font-weight-medium">Adding all notices</div>
                  <div class="text-caption text--secondary">
                    to {{ pipelinesToAdd.length }} {{ pipelinesToAdd.length === 1 ? 'pipeline' : 'pipelines' }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col v-if="pipelinesToRemove.length > 0" cols="12" md="6" class="pl-md-2 mt-2 mt-md-0">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="error" small>mdi-minus-circle</v-icon>
                <div>
                  <div class="text-caption font-weight-medium">Removing all notices</div>
                  <div class="text-caption text--secondary">
                    from {{ pipelinesToRemove.length }} {{ pipelinesToRemove.length === 1 ? 'pipeline' : 'pipelines' }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="$emit('hide-list-manager', true)" class="mr-2">
          <v-icon left small>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn color="primary" depressed @click="savePipelineSelection" :loading="loading" :disabled="!hasChanges">
          <v-icon left small>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else flat class="ma-0 pa-0">
      <v-card-title class="pa-4">No pipelines found</v-card-title>
      <v-card-text class="pa-4">
        <p class="mb-3">
          Create pipelines to organize and track tenders and contracts.
          Pipelines help you manage your opportunities efficiently.
        </p>
        <v-btn color="primary" outlined :to="{ name: 'pipelines' }" @click="$emit('hide-list-manager', true)">
          <v-icon left small>mdi-plus</v-icon>
          Create Your First Pipeline
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { getPipelines, updatePipelines, addPipeline } from './APIManager'

export default {
  props: {
    // Array of tender objects or tender IDs to add to pipelines
    tenders: {
      type: Array,
      required: true,
      default: () => []
    },
    // Array of pipeline IDs that are pre-selected (pipelines that already contain some of the tenders)
    initialSelectedLists: {
      type: Array,
      default: () => []
    },
    changed: Number
  },

  data() {
    return {
      // Array of all available pipelines (fetched internally)
      lists: [],
      // Array of pipeline IDs that should have all tenders added
      pipelinesToAdd: [],
      // Array of pipeline IDs that should have all tenders removed
      pipelinesToRemove: [],
      loading: false,
      newPipelineName: '',
      newPipelinePanel: null,
      searchQuery: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    };
  },

  async mounted() {
    // Fetch lists on mount
    await this.loadPipelines();
    // Initialize based on which pipelines already contain all the tenders
    this.initializePipelineSelections();
  },

  watch: {
    // Watch for changes to trigger refresh (when pipelines are added/deleted elsewhere)
    changed() {
      this.refreshPipelineState();
    },
    // Watch for changes to selected tenders
    tenders: {
      handler() {
        this.refreshPipelineState();
      },
      deep: true
    }
  },

  computed: {
    filteredLists() {
      if (!this.searchQuery) {
        return this.lists;
      }
      const query = this.searchQuery.toLowerCase();
      return this.lists.filter(pipeline =>
        pipeline.name.toLowerCase().includes(query)
      );
    },

    hasChanges() {
      // Check if there are any pipelines to add or remove
      return this.pipelinesToAdd.length > 0 || this.pipelinesToRemove.length > 0;
    },

    // Get tender IDs from objects or return IDs directly
    tenderIds() {
      return this.tenders.map(tender => {
        return typeof tender === 'object' && tender !== null ? parseInt(tender.id) : parseInt(tender);
      });
    },

    // Get tender objects (if passed as objects) or create minimal objects from IDs
    tenderObjects() {
      return this.tenders.map(tender => {
        if (typeof tender === 'object' && tender !== null) {
          return tender;
        }
        return { id: parseInt(tender), title: `Tender #${tender}` };
      });
    }
  },

  methods: {
    async refreshPipelineState() {
      this.loading = true;
      // Reset state arrays
      this.pipelinesToAdd = [];
      this.pipelinesToRemove = [];
      // Reload pipelines to get latest state
      await this.loadPipelines();
      // Reinitialize based on current pipeline state
      this.initializePipelineSelections();
      this.loading = false;
    },

    initializePipelineSelections() {
      // Reset arrays first to avoid stale state
      // Don't pre-select pipelines that already contain all tenders
      this.pipelinesToAdd = [];
      this.pipelinesToRemove = [];
    },

    hasAllTendersInPipeline(pipeline) {
      if (!pipeline.search_query || !pipeline.search_query.tender_id) {
        return false;
      }
      const pipelineTenderIds = pipeline.search_query.tender_id.map(id => parseInt(id));
      return this.tenderIds.every(tenderId =>
        pipelineTenderIds.includes(tenderId)
      );
    },

    hasAnyTendersInPipeline(pipeline) {
      if (!pipeline.search_query || !pipeline.search_query.tender_id) {
        return false;
      }
      const pipelineTenderIds = pipeline.search_query.tender_id.map(id => parseInt(id));
      return this.tenderIds.some(tenderId =>
        pipelineTenderIds.includes(tenderId)
      );
    },

    getTenderCountInPipeline(pipeline) {
      if (!pipeline.search_query || !pipeline.search_query.tender_id) {
        return 0;
      }
      const pipelineTenderIds = pipeline.search_query.tender_id.map(id => parseInt(id));
      return this.tenderIds.filter(tenderId =>
        pipelineTenderIds.includes(tenderId)
      ).length;
    },

    getTendersInPipeline(pipeline) {
      if (!pipeline.search_query || !pipeline.search_query.tender_id) {
        return [];
      }
      const pipelineTenderIds = pipeline.search_query.tender_id.map(id => parseInt(id));
      // Return tender objects that are in this pipeline
      return this.tenderObjects.filter(tender =>
        pipelineTenderIds.includes(parseInt(tender.id))
      );
    },

    toggleAddToPipeline(pipelineId) {
      // If already in add list, remove it (toggle off)
      if (this.pipelinesToAdd.includes(pipelineId)) {
        this.pipelinesToAdd = this.pipelinesToAdd.filter(id => id !== pipelineId);
      } else {
        // Remove from remove list if it's there
        this.pipelinesToRemove = this.pipelinesToRemove.filter(id => id !== pipelineId);
        // Add to add list
        this.pipelinesToAdd.push(pipelineId);
      }
    },

    toggleRemoveFromPipeline(pipelineId) {
      // If already in remove list, remove it (toggle off)
      if (this.pipelinesToRemove.includes(pipelineId)) {
        this.pipelinesToRemove = this.pipelinesToRemove.filter(id => id !== pipelineId);
      } else {
        // Remove from add list if it's there
        this.pipelinesToAdd = this.pipelinesToAdd.filter(id => id !== pipelineId);
        // Add to remove list
        this.pipelinesToRemove.push(pipelineId);
      }
    },

    getPipelineItemClass(pipeline) {
      if (this.pipelinesToAdd.includes(pipeline.id)) {
        return 'pipeline-item-all-selected';
      } else if (this.pipelinesToRemove.includes(pipeline.id)) {
        return 'pipeline-item-to-remove';
      }
      return '';
    },

    getBookmarkIconColor(pipeline) {
      const hasAll = this.hasAllTendersInPipeline(pipeline);
      const hasAny = this.hasAnyTendersInPipeline(pipeline);
      const willAdd = this.pipelinesToAdd.includes(pipeline.id);
      const willRemove = this.pipelinesToRemove.includes(pipeline.id);

      // Keep gray for potential additions (will add but not all in yet)
      if (willAdd && !hasAll) {
        return 'grey';
      }
      // Use status color for all other cases
      return this.getPipelineStatus(pipeline).color;
    },

    getPipelineStatus(pipeline) {
      const hasAll = this.hasAllTendersInPipeline(pipeline);
      const hasAny = this.hasAnyTendersInPipeline(pipeline);
      const willAdd = this.pipelinesToAdd.includes(pipeline.id);
      const willRemove = this.pipelinesToRemove.includes(pipeline.id);

      if (willAdd && hasAll) {
        return {
          text: 'All notices in pipeline',
          color: 'success',
          textColor: 'white',
          icon: 'mdi-check-circle'
        };
      } else if (willAdd && !hasAll) {
        return {
          text: 'Will add all notices',
          color: 'grey',
          textColor: 'white',
          icon: 'mdi-plus-circle'
        };
      } else if (willRemove) {
        return {
          text: 'Will remove all notices',
          color: 'error',
          textColor: 'white',
          icon: 'mdi-minus-circle'
        };
      } else if (hasAll) {
        return {
          text: 'All notices in pipeline',
          color: 'success',
          textColor: 'white',
          icon: 'mdi-check-circle'
        };
      } else if (hasAny) {
        return {
          text: 'Some notices in pipeline',
          color: 'warning',
          textColor: 'white',
          icon: 'mdi-alert-circle'
        };
      } else {
        return {
          text: 'No notices in pipeline',
          color: 'grey',
          textColor: 'white',
          icon: 'mdi-circle-outline'
        };
      }
    },

    async postPipeline() {
      if (!this.newPipelineName.trim()) {
        this.snackbarMessage = "Please enter a valid pipeline name.";
        this.snackbarColor = "warning";
        this.snackbar = true;
        return;
      }

      const existingPipeline = this.lists.find(pipeline =>
        pipeline.name.toLowerCase() === this.newPipelineName.trim().toLowerCase()
      );

      if (existingPipeline) {
        this.snackbarMessage = "Pipeline name already exists. Choose a different name.";
        this.snackbarColor = "error";
        this.snackbar = true;
        return;
      }

      this.loading = true;

      try {
        const response = await addPipeline(encodeURIComponent(this.newPipelineName.trim()));

        if (response && response.status === 'success' && response.data) {
          this.newPipelineName = "";
          this.newPipelinePanel = null;
          // Refresh pipeline state after creating new pipeline
          await this.refreshPipelineState();
          this.snackbarMessage = "Pipeline created successfully!";
          this.snackbarColor = "success";
          this.snackbar = true;
        }
        else {
          this.snackbarMessage = "Failed to create pipeline. Please try again.";
          this.snackbarColor = "error";
          this.snackbar = true;
        }
      }
      catch (error) {
        this.snackbarMessage = "Error creating pipeline. Please try again.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
      this.loading = false;
    },

    async loadPipelines() {
      this.loading = true;
      try {
        const results = await getPipelines();
        if (results && results.data) {
          // Parse search queries for each pipeline
          this.lists = results.data.map(pipeline => {
            const parsed = { ...pipeline };
            if (pipeline.search_query) {
              try {
                parsed.search_query = typeof pipeline.search_query === 'string'
                  ? JSON.parse(pipeline.search_query)
                  : pipeline.search_query;
              } catch (e) {
                parsed.search_query = {};
              }
            }
            return parsed;
          }).sort((a, b) => {
            // Sort by name alphabetically (case-insensitive)
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        }
      } catch (error) {
        //////console.error("Error loading pipelines:", error);
        this.snackbarMessage = "Error loading pipelines. Please try again.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
      this.loading = false;
    },

    async savePipelineSelection() {
      this.loading = true;

      try {
        // For each tender, determine which pipelines it should be in
        // Start by finding existing pipeline memberships for each tender
        const tenderPipelineMap = {};

        this.tenderIds.forEach(tenderId => {
          tenderPipelineMap[tenderId] = [];

          // Find all pipelines that currently contain this tender
          this.lists.forEach(pipeline => {
            if (pipeline.search_query && pipeline.search_query.tender_id) {
              const pipelineTenderIds = pipeline.search_query.tender_id.map(id => parseInt(id));
              if (pipelineTenderIds.includes(parseInt(tenderId))) {
                // Only include if not in pipelinesToRemove
                if (!this.pipelinesToRemove.includes(pipeline.id)) {
                  tenderPipelineMap[tenderId].push(pipeline.id);
                }
              }
            }
          });

          // Add all pipelines that are in pipelinesToAdd (if not already included)
          this.pipelinesToAdd.forEach(pipelineId => {
            if (!tenderPipelineMap[tenderId].includes(pipelineId)) {
              tenderPipelineMap[tenderId].push(pipelineId);
            }
          });
        });

        // Note: pipelinesToRemove means we don't add those pipelines to the list
        // We've already excluded them above when building the map

        // Call updatePipelines for each tender with its list of pipelines
        // Use allSettled to continue even if some fail
        const updatePromises = Object.keys(tenderPipelineMap).map(async tenderId => {
          try {
            await updatePipelines(parseInt(tenderId), tenderPipelineMap[tenderId]);
            return { tenderId, success: true };
          } catch (error) {
            //console.error(`Failed to update pipeline for tender ${tenderId}:`, error);
            return { tenderId, success: false, error };
          }
        });
        
        const results = await Promise.allSettled(updatePromises);
        const failedUpdates = [];
        
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            failedUpdates.push(Object.keys(tenderPipelineMap)[index]);
          } else if (result.value && !result.value.success) {
            failedUpdates.push(result.value.tenderId);
          }
        });
        
        if (failedUpdates.length > 0) {
          //console.warn('Some pipeline updates failed:', failedUpdates);
          // If all failed, throw an error
          if (failedUpdates.length === Object.keys(tenderPipelineMap).length) {
            throw new Error('Failed to update pipelines. One or more tender IDs may not exist in the database.');
          }
          // If some failed, show a warning but continue
          this.snackbarMessage = `Warning: ${failedUpdates.length} of ${Object.keys(tenderPipelineMap).length} tender${Object.keys(tenderPipelineMap).length > 1 ? 's' : ''} could not be updated.`;
          this.snackbarColor = "warning";
          this.snackbar = true;
        }
        
        // Refresh pipeline state after saving changes
        await this.refreshPipelineState();

        // Emit event to notify parent component
        this.$emit('pipeline-updated', {
          tenders: this.tenderIds,
          pipelinesToAdd: this.pipelinesToAdd,
          pipelinesToRemove: this.pipelinesToRemove
        });

        const addCount = this.pipelinesToAdd.length;
        const removeCount = this.pipelinesToRemove.length;
        let message = `Successfully updated pipelines`;
        if (addCount > 0 && removeCount > 0) {
          message += `: Added all tenders to ${addCount} ${addCount === 1 ? 'pipeline' : 'pipelines'}, removed all tenders from ${removeCount} ${removeCount === 1 ? 'pipeline' : 'pipelines'}`;
        } else if (addCount > 0) {
          message += `: Added all tenders to ${addCount} ${addCount === 1 ? 'pipeline' : 'pipelines'}`;
        } else if (removeCount > 0) {
          message += `: Removed all tenders from ${removeCount} ${removeCount === 1 ? 'pipeline' : 'pipelines'}`;
        }

        this.snackbarMessage = message;
        this.snackbarColor = "success";
        this.snackbar = true;

        // Close dialog after a short delay
        setTimeout(() => {
          this.$emit('hide-list-manager', true);
        }, 1500);
      } catch (error) {
        //console.error("Error saving pipeline selections:", error);
        let errorMessage = "Error saving pipeline selections. Please try again.";
        
        // Provide more specific error messages
        if (error.message && error.message.includes('tender_id')) {
          errorMessage = "One or more tender IDs are invalid or don't exist in the database. Please refresh the page and try again.";
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.snackbarMessage = errorMessage;
        this.snackbarColor = "error";
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.pipeline-card {
  height: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.pipeline-card-even {
  background-color: #ffffff !important;
}

.pipeline-card-odd {
  background-color: #f5f5f5 !important;
}

.pipeline-item-all-selected {
  background: linear-gradient(135deg, rgba(78, 184, 136, 0.1) 0%, rgba(78, 184, 136, 0.05) 100%) !important;
  border-left: 4px solid #4EB888 !important;
}

.pipeline-item-to-remove {
  background: linear-gradient(135deg, rgba(255, 20, 99, 0.1) 0%, rgba(255, 20, 99, 0.05) 100%) !important;
  border-left: 4px solid #FF1463 !important;
}

.change-summary {
  border-left: 4px solid #4EB888;
}

.pipeline-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.pipeline-list::-webkit-scrollbar {
  width: 8px;
}

.pipeline-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.pipeline-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.pipeline-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.min-width-0 {
  min-width: 0;
}
</style>
